import { cn } from '@/lib/utils'
import type { IssueCategoryNode } from '@/modules/issues/types.graphl'
import { dateFromStringWithoutTimezone } from '@/modules/timesheets/util'
import { removeCurrentYear } from '@/utils/date'
import { format, formatDistanceToNow } from '@/utils/date-fns'
import { Link } from 'react-router'

interface IssueDateCellProps {
  issueId: string
  timeOfIssue: string
}

export function IssueDateCell({ issueId, timeOfIssue }: IssueDateCellProps) {
  const date = dateFromStringWithoutTimezone(timeOfIssue)

  return (
    <Link
      className="block text-inherit hover:text-secondary hover:no-underline"
      to={`/issues/${issueId}`}
    >
      <div className="font-semibold whitespace-nowrap">
        {removeCurrentYear(format(new Date(timeOfIssue), 'PPP'))}
      </div>

      <div className="text-xs font-medium text-stone-500 whitespace-nowrap">
        {formatDistanceToNow(date, { addSuffix: true })}
      </div>
    </Link>
  )
}

interface IssueReferenceCellProps {
  reference: string | null
}

export function IssueReferenceCell({ reference }: IssueReferenceCellProps) {
  if (!reference) return '-'

  return (
    <span className="font-semibold tracking-wider whitespace-nowrap">
      {reference}
    </span>
  )
}

interface VehiclePlateProps {
  plate: string | null
}

export function VehiclePlate({ plate }: VehiclePlateProps) {
  if (!plate) return '-'

  return (
    <span className="inline-block rounded-md border border-stone-300 px-2.5 py-1 text-sm font-semibold whitespace-nowrap">
      {plate}
    </span>
  )
}

interface IssueCategoryBadgeProps {
  category: Pick<IssueCategoryNode, 'id' | 'color' | 'name'>
}

export function IssueCategoryBadge({ category }: IssueCategoryBadgeProps) {
  return (
    <span
      className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold whitespace-nowrap"
      style={{ background: category.color ?? 'var(--color-secondary)' }}
    >
      {category.name}
    </span>
  )
}

interface IssueStatusCellProps {
  closed: boolean
  archived: boolean
  edited: boolean

  openText: string
  editText: string
  archivedText: string
  closedText: string
}

export function IssueStatusCell({
  closed,
  archived,
  edited,

  openText,
  editText,
  archivedText,
  closedText,
}: IssueStatusCellProps) {
  const { label, dot } = archived
    ? { label: archivedText, dot: 'before:bg-orange-500' }
    : closed
    ? { label: closedText, dot: 'before:bg-lime-600' }
    : edited
    ? { label: editText, dot: 'before:bg-yellow-500' }
    : { label: openText, dot: 'before:bg-red-600' }

  return (
    <div
      className={cn(
        'text-sm font-semibold whitespace-nowrap',
        "before:content-[''] before:inline-block before:size-2 before:mr-2 before:mb-px before:rounded-full",
        dot
      )}
    >
      {label}
    </div>
  )
}
